import React, { useEffect, useMemo, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import WeeklyScheduleView from "./WeeklyScheduleView";
import { useNotes } from "../../hooks/useNotes";
import DashboardGeneralNotes from "./DashboardGeneralNotes";
import ChatAgentWidget from "../AgentWidget/AgentChatWidet";
import { CalendarSearch } from "lucide-react";
import useDayCarousel from "../../hooks/useDayCarousel";
import WeekdayBar from "./WeekdayBar";
import DayCarousel from "./DayCarousel";

const MainDashboard = ({ auth }) => {
  const workspaceId = localStorage.getItem("workspaceId");
  const { notes, fetchNotesGeneral, addNote, updateNote, deleteNote } = useNotes({
    workspaceId,
    token: auth.user?.id_token,
  });

  const {
    focusedDate,
    effectiveStartDate,
    selectedMonth,
    selectedYear,
    cardsPerViewport,
    currentWeekDays,
    focusedWeekdayIndex,
    weekOffset,
    goToDate,
    goToToday,
    goToNext,
    goToPrev,
    goToNextWeek,
    goToPrevWeek,
    handleMonthYearChange,
  } = useDayCarousel();

  useEffect(() => { fetchNotesGeneral(); }, [fetchNotesGeneral]);

  const groupedNotes = useMemo(() => {
    if (!notes) return {};
    return notes.reduce((acc, note) => {
      if (!(note.isGeneral || note.is_general)) return acc;
      const cat = note.category || "Allgemein";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(note);
      return acc;
    }, {});
  }, [notes]);

  const handleDayTapped = useCallback((index) => {
    goToDate(currentWeekDays[index]);
  }, [currentWeekDays, goToDate]);

  const handleCarouselFocusChange = useCallback((date) => {
    goToDate(date);
  }, [goToDate]);

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ background: "#f5f8fc" }}>
      <div className="flex-grow-1 dashboard-main-content d-flex flex-column">
        <div className="d-flex flex-column gap-3 flex-grow-1">
          {/* Header: Notes + Month/Year Picker */}
          <div className="flex-shrink-0 p-3">
            <div className="d-flex flex-column flex-md-row gap-3 align-items-start">
              <div className="flex-grow-1 w-100">
                <DashboardGeneralNotes
                  groupedNotes={groupedNotes}
                  softBlue="#5b7db1"
                  cardBg="#ffffff"
                  addNote={addNote}
                  updateNote={updateNote}
                  deleteNote={deleteNote}
                  workspaceId={workspaceId}
                  onNoteChanged={() => window.location.reload()}
                />
              </div>

              <div className="d-flex flex-wrap align-items-center gap-3 justify-content-end">
                <div className="d-flex align-items-center px-3 py-1 rounded-pill bg-light border">
                  <CalendarSearch size={16} className="text-muted me-2" />
                  <select className="border-0 bg-transparent fw-bold text-dark" style={{ fontSize: '0.9rem' }} value={selectedMonth} onChange={(e) => handleMonthYearChange(Number(e.target.value), selectedYear)}>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <option key={i} value={i}>{new Date(0, i).toLocaleString("default", { month: "short" })}</option>
                    ))}
                  </select>
                  <span className="mx-1 text-muted opacity-25">|</span>
                  <select className="border-0 bg-transparent fw-bold text-dark" style={{ fontSize: '0.9rem' }} value={selectedYear} onChange={(e) => handleMonthYearChange(selectedMonth, Number(e.target.value))}>
                    {[2023, 2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={goToToday}>Heute</button>
              </div>
            </div>
          </div>

          {/* Weekday Bar */}
          <WeekdayBar
            weekDays={currentWeekDays}
            focusedIndex={focusedWeekdayIndex}
            cardsPerViewport={cardsPerViewport}
            onDayTapped={handleDayTapped}
            onPrevWeek={goToPrevWeek}
            onNextWeek={goToNextWeek}
          />

          {/* Day Carousel */}
          <div className="flex-grow-1 d-flex flex-column">
            <WeeklyScheduleView
              auth={auth}
              weekOffset={weekOffset}
              startDate={effectiveStartDate}
              daysCount={cardsPerViewport}
              onNoteChanged={fetchNotesGeneral}
              renderDayColumns={({ dayColumns, days, loading }) => {
                if (loading) return <div className="p-5 text-center">Loading Schedule...</div>;
                return (
                  <DayCarousel
                    days={days.map(d => d.date)}
                    cardsPerViewport={cardsPerViewport}
                    focusedDate={effectiveStartDate}
                    onFocusChange={handleCarouselFocusChange}
                    onPrev={goToPrev}
                    onNext={goToNext}
                  >
                    {dayColumns}
                  </DayCarousel>
                );
              }}
            />
          </div>
        </div>
      </div>

      <ChatAgentWidget auth={auth} />
    </div>
  );
};

export default MainDashboard;
